import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultDetailsReportFilters } from '../../../constants/reprots';
import { reportsStore } from '../../../store/reportsStore';
import {
  IAgentsReportParams,
  IComboReportParams,
  IDetailsReportFilters,
  IDetailsReports,
  IItemsReportParams,
  IOrderingChannelsReportParams,
  IPaymentMethodsParams,
} from '../types/salesDetails';
import { PAYMENT_TYPES, allOptionsValue, paymentTypesLangKeys, defaultPagination } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import wms from 'src/services/wms';
import { downloadFile, getCancelToken, getTranslate } from 'src/utils/helpers';
import { ORDERING_CHANNELS, orderingChannelsLangKeys } from '~/constants/orderingChannels';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { IPagination } from '~/types/main';
import { IProductV4 } from '~/types/product';

export class SalesDetailsStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancelReportBuild: {
    [key: string]: null | Canceler;
  } = {
    agents: null,
    items: null,
    orderingChannels: null,
    paymentMethods: null,
  };

  @observable filters: IDetailsReportFilters = defaultDetailsReportFilters;
  @observable reports: IDetailsReports = {
    agents: {
      data: undefined,
      loading: false,
    },
    items: {
      data: undefined,
      loading: false,
    },
    orderingChannels: {
      data: undefined,
      loading: false,
    },
    paymentMethods: {
      data: undefined,
      loading: false,
    },
    combo: {
      data: undefined,
      loading: false,
    },
  };

  @action setFilters = (filters: Partial<IDetailsReportFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action fetchAgentsReport = async () => {
    try {
      const params: IAgentsReportParams = {
        ...reportsStore.getReportBuildParams(),
        agents: this.filters.agents.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.agents.map((option) => option.value as number),
      };

      this.reports.agents.loading = true;
      const agentsReport: { [key: string]: any } = await client.get('v3/reports/sales/call_center_agents', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.agents = c)),
      });

      this.reports.agents.data = Object.entries(agentsReport).map(([_, body]) => {
        const name = localeStore.t(body.agent.name);
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { agent, ...reportBody } = body;
        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesDetailsStore fetchAgentsReport' });
    } finally {
      this.reports.agents.loading = false;
    }
  };

  @action fetchItemsReport = async () => {
    try {
      const params: IItemsReportParams = {
        ...reportsStore.getReportBuildParams(),
        items: this.filters.items.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.items.map((option) => option.value as number),
      };

      this.reports.items.loading = true;
      const itemsReport: { [key: string]: any } = await client.get('v3/reports/sales/items', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.items = c)),
      });

      this.reports.items.data = Object.entries(itemsReport).map(([_, body]) => {
        const name = getTranslate(body.item.name);
        const reportBody: any = {
          orders_count: body.items_count,
          pickup_count: body.pickup_items_count,
          delivery_count: body.delivery_items_count,
          dinein_count: body.dinein_items_count,
        };

        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesDetailsStore fetchItemsReport' });
    } finally {
      this.reports.items.loading = false;
    }
  };

  @action fetchComboReport = async () => {
    try {
      const params: IComboReportParams = {
        ...reportsStore.getReportBuildParams(),
        combo: this.filters.combo.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.combo.map((option) => option.value as number),
      };

      this.reports.combo.loading = true;
      const combosReport: { [key: string]: any } = await client.get('v3/reports/sales/combos', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.combo = c)),
      });
      this.reports.combo.data = Object.entries(combosReport).map(([_, body]) => {
        const name = getTranslate(body.combo.name);
        const reportBody: any = {
          orders_count: Number(body.combo_count),
          pickup_count: Number(body.pickup_combo_count),
          delivery_count: Number(body.delivery_combo_count),
          dinein_count: Number(body.dinein_combo_count),
          combo_price: Number(body.combo_price),
        };

        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesDetailsStore fetchComboReport' });
    } finally {
      this.reports.combo.loading = false;
    }
  };

  @action fetchComboNames = async (page: number, searchValue: string = ''): Promise<IPagination<IProductV4>> => {
    try {
      const pagination: IPagination<IProductV4> = await client.get('v4/products?types[]=2', {
        params: { page, name: searchValue.toLowerCase(), is_delete: 0 },
      });
      return pagination;
    } catch (error: any) {
      logger.error({ error, place: 'SalesDetailsStore fetchComboNames' });
      return defaultPagination;
    }
  };

  @action onExportComboReport = async () => {
    const params: IComboReportParams = {
      ...reportsStore.getReportBuildParams(),
      combo: this.filters.combo.some((option) => option.value === allOptionsValue) ? [] : this.filters.combo.map((option) => option.value as number),
    };
    await this.exportDetailsReportToXls(params, 'combo', 'combos', 'Combo sales');
  };

  @action fetchOrderingChannelsReport = async () => {
    try {
      const params: IOrderingChannelsReportParams = {
        ...reportsStore.getReportBuildParams(),
        channels: this.filters.orderingChannels.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.orderingChannels.map((option) => option.value as ORDERING_CHANNELS),
      };

      this.reports.orderingChannels.loading = true;
      const orderingChannelsReport: { [key: string]: any } = await client.get('v3/reports/sales/channels', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.orderingChannels = c)),
      });

      this.reports.orderingChannels.data = Object.entries(orderingChannelsReport).map(([_, body]) => {
        const name = localeStore.t(orderingChannelsLangKeys[body.channel_id as ORDERING_CHANNELS]);
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { channel_id, ...reportBody } = body;
        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesDetailsStore fetchOrderingChannelsReport' });
    } finally {
      this.reports.orderingChannels.loading = false;
    }
  };

  @action fetchPaymentMethodsReport = async () => {
    try {
      const params: IPaymentMethodsParams = {
        ...reportsStore.getReportBuildParams(),
        payment_methods: this.filters.paymentMethods.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.paymentMethods.map((option) => option.value as PAYMENT_TYPES),
      };

      this.reports.paymentMethods.loading = true;
      const paymentMethodsReport: { [key: string]: any } = await client.get('v3/reports/sales/payment-methods', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.paymentMethods = c)),
      });

      this.reports.paymentMethods.data = Object.entries(paymentMethodsReport).map(([_, body]) => {
        const name = localeStore.t(paymentTypesLangKeys[body.method_id as PAYMENT_TYPES]);
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { method_id, ...reportBody } = body;
        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesDetailsStore fetchPaymentMethodsReport' });
    } finally {
      this.reports.paymentMethods.loading = false;
    }
  };

  @action exportDetailsReportToXls = async (
    params: IItemsReportParams | IAgentsReportParams | IPaymentMethodsParams | IOrderingChannelsReportParams | IComboReportParams,
    reportKey: keyof IDetailsReports,
    urlPath: string,
    fileName: string
  ) => {
    try {
      this.reports[reportKey].loading = true;
      const detailsReport = await client.get(`v3/reports/sales/${urlPath}`, {
        params: {
          ...params,
          export_xls: 1,
          export_xls_url: window.IS_MOBILE ? 1 : 0,
        },
        cancelToken: getCancelToken((c) => (this.cancelReportBuild[reportKey] = c)),
        responseType: window.IS_MOBILE ? 'text' : 'blob',
      });
      this.reports[reportKey].loading = false;

      if (window.IS_MOBILE) {
        wms.downloadFile(detailsReport);
      } else {
        downloadFile(detailsReport, `${fileName}, ${moment().format('DD-MM-YYYY')}.xls`);
      }

      toastSuccess({ message: localeStore.t('toast_report_exported') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports[reportKey].loading = false;
      logger.error({ error, place: 'SalesDetailsStore exportDetailsReportToXls' });
    }
  };
}

export const salesDetailsStore = new SalesDetailsStore();
